import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

// Components
import { TableView, TextArea, Button, Checkbox, InputNumeric, DynamicTooltip, Icons } from "@gpstrackit/gtc-react-controls";
import { NotificationManager, NotificationContainer } from "Components/Notifications";
import { Timezone } from 'Components/uiControls';
import { Grid } from "@mui/material";
import { Card } from '@mui/material';
import HistoryTooltip from './modals/historyTooltip';
import DateRangeInfo from './modals/dateRangeModal';

// Actions
import { searchPerformanceImprovement, updateDriverPerformanceCase, searchRiskHistory } from './redux/slice';

//Styles
import './index.scss';

const ImplovementPlanTab = (props) => {
    const {
        notes,
        changeNotes,
        improvementOmit,
        targetValues,
        onNotesChange,
        onImprovementOmitChange,
        onTargetChange,
        onNotesCancel,
        resetTabs
    } = props;

    const { messages } = useIntl();
    const dispatch = useDispatch();

    // Redux states
    const caseDetail = useSelector((state) => state.driverPerformanceCaseDetailRedux.caseDetail);
    const performanceImprovement = useSelector((state) => state.driverPerformanceImprovementPlanTabRedux.performanceImprovement);
    const loadingPerformanceImprovement = useSelector((state) => state.driverPerformanceImprovementPlanTabRedux.loadingPerformanceImprovement);
    const updateDriverPerformanceCaseResult = useSelector((state) => state.driverPerformanceImprovementPlanTabRedux.updateDriverPerformanceCase);
    const loadingUpdateDriverPerformanceCase = useSelector((state) => state.driverPerformanceImprovementPlanTabRedux.loadingUpdateDriverPerformanceCase);
    const riskHistory = useSelector((state) => state.driverPerformanceImprovementPlanTabRedux.riskHistory);
    const loadingRiskHistory = useSelector((state) => state.driverPerformanceImprovementPlanTabRedux.loadingRiskHistory);
    const dateRange = useSelector((state) => state.driverPerformanceImprovementPlanTabRedux.dateRange);
    const user = useSelector(state => state.securityRedux.user);
    const { isKph, dateformat, timeZone } = user;

    // Refs
    const performanceImprovementTable = useRef();

    // Validation function for target inputs
    const validateTargetValue = (value, rowKey) => {
        // Handle null/undefined/empty values
        if (value === null || value === undefined || value === '') {
            return null;
        }

        // Convert to string first to handle edge cases
        let stringValue = String(value);

        // Remove any negative signs immediately
        if (stringValue.includes('-')) {
            stringValue = stringValue.replace(/-/g, '');
        }

        // Convert to number
        const numValue = parseFloat(stringValue);

        // Check if it's a valid number
        if (isNaN(numValue)) {
            return null; // Return null for invalid numbers
        }

        // Ensure only positive values (this should never happen now, but just in case)
        if (numValue < 0) {
            return 0; // Convert negative to 0
        }

        // Ensure max value is 5000
        if (numValue > 5000) {
            return 5000; // Limit to 5000
        }

        return numValue; // Return valid value
    };

    useEffect(() => {
        if (caseDetail && !caseDetail?.updateCoach) {
            if (caseDetail?.id && caseDetail?.driverId) {
                dispatch(searchPerformanceImprovement({ caseId: caseDetail?.id, driverId: caseDetail?.driverId }))
            }
            if (caseDetail?.caseNumber) {
                dispatch(searchRiskHistory({ caseNumber: caseDetail?.caseNumber }));
            }
        }
    }, [caseDetail]);

    const columnsRisk = [
        { title: messages['driver_performance_case_overview_eventName'], key: 'event', width: 150, render: (row) => <div>{row?.eventName ? messages[`driver_performance_${row?.eventName?.replace(/\s+/g, "")}`] || row?.eventName : 'N/A'}</div>, show: true, },
        { title: messages['driver_performance_case_overview_count'], key: 'count', width: 100, render: (row) => <div>{row?.events?.length || 0}</div>, show: true },
        { title: messages['driver_performance_case_overview_priorCoaching'], key: 'priorCoaching', width: 130, render: (row) => <div>{row?.priorCoaching ? messages['driver_performance_implovementPlan_yes'] : messages['driver_performance_implovementPlan_no']}</div>, show: true },
    ];

    const columnsPerformanceImprovement = [
        {
            title: messages['driver_performance_implovementPlan_metric'],
            key: 'metric',
            width: 120,
            render: (row) => <div>{messages[`driver_performance_implovementPlan_${row.key}`] || row.key}</div>,
            show: true
        },
        {
            title: messages['driver_performance_implovementPlan_measure'],
            key: 'measure',
            width: 100,
            render: (row) => {
                let value = row.value;
                if (isKph && row.key === 'totalDistance') {
                    if (parseInt(row.value) > 0) {
                        value = (row.value * 1.609344);
                    }
                }
                // Format float values to show only 2 decimal places
                const formattedValue = value !== null && value !== undefined
                    ? parseFloat(value).toFixed(2)
                    : value;
                return <div>{`${formattedValue} ${row.key === 'totalDistance' ? isKph ? messages['kilometers'] : messages["miles"] : ''}`}</div>;
            },
            show: true
        },
        {
            title: messages['driver_performance_implovementPlan_target'],
            key: 'target',
            width: 100,
            render: (row) => {
                let value = targetValues[row.key];
                if ((improvementOmit || caseDetail?.statusId?.includes('completed') || caseDetail?.statusId?.includes('evaluate')) && isKph && row.key === 'totalDistance') {
                    if (parseFloat(value) > 0) {
                        value = (value * 1.609344);
                    }
                    value = value.toFixed(2);
                }
                return (
                    <div className="target-input-container" style={{ textAlign: 'center' }}>
                        <InputNumeric
                            key={row.key}
                            value={value || null}
                            onChange={(value) => {
                                const validatedValue = validateTargetValue(value);
                                onTargetChange(row.key, validatedValue);
                            }}
                            isDisabled={improvementOmit || caseDetail?.statusId?.includes('completed') || caseDetail?.statusId?.includes('evaluate')}
                            onlyPositive
                            decimalPlaces={2}
                            maxLength={4}
                            min={0}
                            max={5000}
                        />
                    </div>
                )
            },
            show: true
        },
        {
            title: messages['driver_performance_implovementPlan_success'],
            key: 'success',
            width: 100,
            render: (row) => {
                let isChecked = false;
                if (row.key == 'avgEvents') {
                    isChecked = caseDetail?.targetAvgEventsSuccess || false
                }
                else if (row.key == 'riskScore') {
                    isChecked = caseDetail?.targetRiskScoreSuccess || false
                }
                else if (row.key == 'totalEvents') {
                    isChecked = caseDetail?.targetEventsSuccess || false
                }
                return (
                    <div>
                        {
                            row.key == 'totalDistance' ? <span style={{ fontWeight: 500 }}>{messages['driver_performance_implovementPlan_byDistance']}</span> : (
                                <Checkbox
                                    checked={isChecked}
                                    isDisabled={loadingUpdateDriverPerformanceCase}
                                />
                            )
                        }
                    </div>
                )
            },
            show: true
        },
    ];

    const saveNotes = () => {
        dispatch(updateDriverPerformanceCase({ id: caseDetail?.id, notes, statusId: caseDetail?.statusId }))
    }

    const omitPerformanceImprovement = (value) => {
        onImprovementOmitChange(value);
    }

    useEffect(() => {
        if (updateDriverPerformanceCaseResult) {
            if(updateDriverPerformanceCaseResult?.updateStatus) {
                resetTabs();
            }
            // Reset changeNotes state through parent
            onNotesCancel();
            NotificationManager.success(
                messages['driver_performance_implovementPlan_updatedCase'],
                messages["success"],
                3000
            );
        }
    }, [updateDriverPerformanceCaseResult])


    return (
        <Grid container spacing={2} style={{ marginTop: 5 }}>
            <Grid item xs={12} md={6}>
                <Card style={{ padding: 16, height: 390 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <span style={{ fontWeight: 600 }}>{messages['driver_performance_implovementPlan_risk']}</span>
                            <div style={{ marginTop: 10 }}>
                                <TableView
                                    loading={loadingRiskHistory}
                                    columns={columnsRisk}
                                    total={0}
                                    disableItemSelection
                                    hideDragable
                                    hideAllInPagination
                                    data={riskHistory || []}
                                    height={390}
                                    topSpaceSize={300}
                                    hidePagination
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card style={{ padding: 16, height: 390 }}><Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <HistoryTooltip historyData={caseDetail?.evaluateValues}>
                                    <span style={{
                                        fontFamily: 'Outfit, sans-serif',
                                        cursor: 'pointer',
                                        fontSize: 14,
                                        fontWeight: 400,
                                        borderBottom: '1px dashed #595151cc',
                                        display: 'inline-block'
                                    }}>{messages['driver_performance_implovementPlan_performanceImprovement']}</span>
                                </HistoryTooltip>
                                {dateRange && (
                                    <DynamicTooltip
                                        trigger={<Icons name='info' style={{ fontSize: 16, color: '#666', cursor: 'pointer', display: 'flex' }} />}
                                        openOn="hover"
                                        size="small"
                                        position="top"
                                    >
                                        {() => (
                                            <DateRangeInfo
                                                dateRange={dateRange}
                                                dateformat={dateformat}
                                                timeZone={timeZone}
                                            />
                                        )}
                                    </DynamicTooltip>
                                )}
                            </div>
                            <Checkbox
                                label={messages['driver_performance_implovementPlan_omit']}
                                checked={improvementOmit}
                                onChange={(e) => {
                                    omitPerformanceImprovement(e)
                                }}
                                isDisabled={loadingUpdateDriverPerformanceCase || caseDetail?.statusId?.includes('completed') || caseDetail?.statusId?.includes('evaluate') || loadingPerformanceImprovement}
                            />
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <TableView
                                ref={performanceImprovementTable}
                                columns={columnsPerformanceImprovement}
                                data={performanceImprovement}
                                loading={loadingPerformanceImprovement}
                                total={4}
                                disableItemSelection
                                hideDragable
                                height={376}
                                topSpaceSize={300}
                                hidePagination
                            />

                        </div>
                    </Grid>
                </Grid>
                </Card>
            </Grid>
            <Grid item xs={12}>
                <Card style={{ padding: 16, height: 200 }}><Grid container spacing={2}>
                    <Grid item xs={12}>
                        <span style={{ fontWeight: 600 }}>{messages['driver_performance_implovementPlan_notes']}</span>
                        <TextArea
                            value={notes}
                            onChange={onNotesChange}
                            style={{ height: 110, marginTop: 10, resize: 'none' }}
                            maxLength={500}
                            isDisabled={caseDetail?.statusId?.includes('completed')}
                        />
                        <div style={{ display: 'flex', justifyContent: 'end', marginTop: 10 }}>
                            <Button
                                variant="secondary"
                                onClick={onNotesCancel}
                                isDisabled={!changeNotes || loadingUpdateDriverPerformanceCase}
                                style={{ marginRight: 8 }}
                            >
                                {messages['cancel']}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={saveNotes}
                                isDisabled={!changeNotes || loadingUpdateDriverPerformanceCase}
                            >
                                {messages['save']}
                            </Button>
                        </div>
                    </Grid>
                </Grid>
                </Card>
            </Grid>
            <NotificationContainer />
        </Grid >
    )
}

export default ImplovementPlanTab;